"use client";

import PropTypes from "prop-types";
import Script from "next/script";
import { useEffect, useMemo } from "react";
import GPTCriteoScript from "components/google-publisher-tags/gpt-criteo-script";
import { adsByPage } from "components/google-publisher-tags/ad-units";

const OUT_OF_PAGE_ENABLED_PATHS = ["/projeler", "/satilik-konut"];
const INLINE_REFRESH_INTERVAL = 30000;

const isOutOfPagePath = (url) => OUT_OF_PAGE_ENABLED_PATHS.some((path) => url?.includes(path));

const isOutOfPageSlot = (slot) => Boolean(slot?.outOfPage || slot?.label?.includes("ej_w_oop"));

const createRuntimeKey = () =>
  process.env.NODE_ENV === "development" ? "__GPT_DEV_STATE" : `__gpt_${Date.now()}_${Math.floor(Math.random() * 1e7)}`;

const defineInlineSlot = (slot) => `
  (function() {
    const id = '${slot.id}';
    const slotDef = googletag.defineSlot('${slot.label}', ${slot.sizes}, id);
    if (slotDef) {
      slotDef.addService(googletag.pubads());
      inlineSlotIds.push(id);
    }
  })();
`;

const defineOutOfPageSlot = (slot) => `
  (function() {
    const id = '${slot.id}';
    outOfPageSlotIds.push(id);
    googletag.defineOutOfPageSlot('${slot.label}', id).addService(googletag.pubads());
  })();
`;

const displayOnRender = (slot) => `
  if (typeof event !== 'undefined' &&
      event.slot.getSlotElementId() === '${slot.id}' && 
      !event.isEmpty) {
    const el = document.getElementById('${slot.id}');
    if (el) el.style.display = 'block';
  }
`;

const serializeValues = (values) => (Array.isArray(values) && values.length ? `["${values.join('","')}"]` : `""`);

const serializeTargeting = ({ label, values, value }) => {
  const finalValue = values ?? value ?? "";
  const formatted = Array.isArray(finalValue) ? serializeValues(finalValue) : `"${finalValue}"`;
  return `.setTargeting("${label}", ${formatted})`;
};

const buildGPTScript = (adSlots, pageUrl, targetings, runtimeKey) => `
  (function() {
    window.googletag = window.googletag || {};
    window.googletag.cmd = window.googletag.cmd || [];
    window.__GPT_STATE = window.__GPT_STATE || {};
    window.__GPT_STATE['${runtimeKey}'] = {
      anchors: new Set(),
      timers: new Set(),
      displayedOutOfPageSlots: new Set(),
    };

    googletag.cmd.push(function() {
      const state = window.__GPT_STATE['${runtimeKey}'];
      const inlineSlotIds = [];
      const outOfPageSlotIds = [];

      ${adSlots
        .filter((slot) => !slot.lazy)
        .map((slot) =>
          isOutOfPageSlot(slot) ? (isOutOfPagePath(pageUrl) ? defineOutOfPageSlot(slot) : "") : defineInlineSlot(slot)
        )
        .join("")}

      ${targetings?.length ? `googletag.pubads()${targetings.map((t) => serializeTargeting(t)).join("")};` : ""}

      googletag.pubads().enableLazyLoad({
        fetchMarginPercent: 100,
        renderMarginPercent: 1,
        mobileScaling: 1.0,
      });
      googletag.pubads().enableSingleRequest();
      googletag.pubads().collapseEmptyDivs(true);
      googletag.pubads().enableVideoAds();
      googletag.companionAds().setRefreshUnfilledSlots(true);
      googletag.enableServices();

      function ensureAnchor(id) {
        let anchor = document.getElementById(id);
        if (!anchor) {
          anchor = document.createElement("div");
          anchor.id = id;
          anchor.setAttribute("data-gpt-anchor", "true");
          anchor.style.cssText =
            "position:absolute;width:1px;height:1px;top:0;left:0;opacity:0;pointer-events:none;";
          document.body.appendChild(anchor);
          state.anchors.add(id);
        }
        return anchor;
      }

      function displayInlineSlots() {
        inlineSlotIds.forEach((id) => {
          ensureAnchor(id);
          googletag.display(id);
        });
      }

      function displayOutOfPageSlotsOnce() {
        outOfPageSlotIds.forEach((id) => {
          if (!state.displayedOutOfPageSlots.has(id)) {
            state.displayedOutOfPageSlots.add(id);
            ensureAnchor(id);
            googletag.display(id);
          }
        });
      }

      function refreshInlineSlots() {
        const allSlots = googletag.pubads().getSlots?.() || [];
        const activeInlineSlots = allSlots.filter((s) =>
          inlineSlotIds.includes(s.getSlotElementId())
        );
        if (activeInlineSlots.length) {
          googletag.pubads().refresh(activeInlineSlots);
        }
      }

      displayOutOfPageSlotsOnce();
      displayInlineSlots();

      googletag.pubads().addEventListener("slotRenderEnded", function(event) {
        ${adSlots
          .filter((slot) => !slot.lazy && !isOutOfPageSlot(slot))
          .map((slot) => displayOnRender(slot))
          .join("")}
      });

      if (inlineSlotIds.length) {
        const timeout = setTimeout(() => {
          refreshInlineSlots();
          const interval = setInterval(refreshInlineSlots, ${INLINE_REFRESH_INTERVAL});
          state.timers.add({ id: interval, type: "interval" });
        }, ${INLINE_REFRESH_INTERVAL});
        state.timers.add({ id: timeout, type: "timeout" });
      }
    });
  })();
`;

function GPTHeader({ pageName, targetingValueList, path }) {
  const adSlots = adsByPage[pageName] || [];
  const runtimeKey = useMemo(() => createRuntimeKey(), []);

  return (
    <>
      <GPTCriteoScript />
      <Script
        id="gpt-pub-ads"
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: buildGPTScript(adSlots, path, targetingValueList, runtimeKey),
        }}
        strategy="afterInteractive"
      />
      <GPTLifecycle adIds={adSlots.filter((slot) => !slot.lazy).map((slot) => slot.id)} runtimeKey={runtimeKey} />
    </>
  );
}

GPTHeader.propTypes = {
  pageName: PropTypes.string,
  targetingValueList: PropTypes.array,
  path: PropTypes.string,
};

function GPTLifecycle({ adIds, runtimeKey }) {
  useEffect(
    () => () => {
      const state = window.__GPT_STATE?.[runtimeKey];
      if (!state) return;

      state.timers.forEach(({ id, type }) => (type === "timeout" ? clearTimeout(id) : clearInterval(id)));
      state.timers.clear?.();

      const allSlots = window.googletag?.pubads?.().getSlots?.() || [];
      const ownedSlots = allSlots.filter((s) => adIds.includes(s.getSlotElementId()));
      if (ownedSlots.length && window.googletag?.destroySlots) {
        window.googletag.destroySlots(ownedSlots);
      }

      state.anchors.forEach((slotId) => {
        const el = document.getElementById(slotId);
        if (el?.getAttribute("data-gpt-anchor") === "true") el.remove();
      });
      state.anchors.clear?.();

      delete window.__GPT_STATE?.[runtimeKey];
    },
    [adIds, runtimeKey]
  );

  return null;
}

GPTLifecycle.propTypes = {
  adIds: PropTypes.arrayOf(PropTypes.string),
  runtimeKey: PropTypes.string,
};

export default GPTHeader;
